import { Star } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useCommonApi } from '../../api';
import { DashboardActionTile } from '../common';
import { BasicDialog } from '../common/BasicDialog';
import { CustomDateTimePicker } from '../common/DateTimePicker';
import { ACCIDENT_SOURCE, AccidentGeneral, AccidentSourcesList, RegistryTypeLabel } from '../types/accident';
import { ACCIDENT_SOURCE_TYPE } from './constants';

interface TipOption {
  id?: number;
  name: string;
  tradeName?: string | null;
  label: string;
  fullName?: string;
  favourite?: boolean;
}

export interface ReasonType {
  id?: number;
  type: string;
  name?: string;
  tradeName?: string;
  address?: string;
  phone?: string;
  bankAccountNumber?: string;
  birthDate?: Date;
  commissionValue?: string;
}

interface DecisionCardProps {
  isAccept: boolean;
  onClick: () => void;
  label: string;
}

interface RejectionContentProps {
  reasonValue?: ReasonType;
  onReasonChange: (value: ReasonType) => void;
  customValue: string;
  onCustomValueChange: (value: string) => void;
  rejectionReasons?: RegistryTypeLabel[];
  accidentDate?: Date | null;
  onDateChange?: (date: Date | null) => void;
  acceptanceReasons?: AccidentSourcesList[];
  tipSourceValue?: ReasonType;
  onTipSourceChange: (value?: ReasonType) => void;
}

interface AcceptanceContentProps {
  reasonValue?: ReasonType;
  onReasonChange: (value: ReasonType) => void;
  insuranceValue?: string;
  onSourceDetailChange: (value: ReasonType | undefined) => void;
  onPlaceValue: number;
  onPlaceChange: (value: number) => void;
  acceptanceReasons?: AccidentSourcesList[];
  accidentDate?: Date | null;
  onDateChange?: (date: Date | null) => void;
  isEdit?: boolean;
}

interface AccidentCreatingStepDecisionProps {
  onVote: (vote: AccidentGeneral) => void;
  isLoading?: boolean;
  acceptanceReasons?: AccidentSourcesList[];
  rejectionReasons?: RegistryTypeLabel[];
}

const DecisionCard: React.FC<DecisionCardProps> = ({ isAccept, onClick, label }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <DashboardActionTile
      tone={isAccept ? 'success' : 'primary'}
      title={label}
      description={isAccept ? 'Pokračovat k založení nehody' : 'Uzavřít bez řešení'}
      icon={isAccept ? <CheckIcon fontSize="large" /> : <ClearIcon fontSize="large" />}
      onClick={onClick}
    />
  </Box>
);

const RegistrySelector: React.FC<{
  acceptanceReasons?: AccidentSourcesList[];
  value?: ReasonType;
  onChange: (value?: ReasonType) => void;
  label?: string;
  registryType: 'tip' | 'service';
  dialogTitle?: string;
}> = ({ acceptanceReasons, value, onChange, label, registryType, dialogTitle }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [onlyAssigned, setOnlyAssigned] = useState(false);
  const [tipOptions, setTipOptions] = useState<TipOption[]>(
    acceptanceReasons
      ?.find((item) => item.type === registryType)
      ?.values?.map((v) => ({
        id: v.id,
        name: v.name ?? '',
        label: v.displayName ?? v.name ?? '',
        fullName: v.name,
        tradeName: v.tradeName,
        favourite: v.favourite
      }))
      .filter((option) => option.name)
      .sort((a, b) => {
        if (a.favourite && !b.favourite) return -1;
        if (!a.favourite && b.favourite) return 1;
        return a.name.localeCompare(b.name);
      }) ?? []
  );
  const [selectedTip, setSelectedTip] = useState<TipOption | null>(
    value?.name
      ? tipOptions.find((opt) => opt.id === value.id && opt.name === value.name) ?? {
          name: value.name,
          label: value.name,
          id: value.id
        }
      : null
  );
  const [newTipValue, setNewTipValue] = useState<
    | {
        name: string;
        tradeName?: string;
        address?: string;
        phone?: string;
        bankAccountNumber?: string;
        birthDate?: Date;
        commissionValue?: string;
      }
    | undefined
  >(undefined);
  const [commissionType, setCommissionType] = useState<string>('Kč');

  const isTipType = registryType === 'tip';

  const { data: assignedSources } = useQuery<AccidentSourcesList>({
    ...useCommonApi().getAccidentSourcesByType(
      isTipType ? ACCIDENT_SOURCE.TIPPER : ACCIDENT_SOURCE.SERVICE,
      undefined,
      true
    ),
    enabled: onlyAssigned
  });

  useEffect(() => {
    const source = onlyAssigned ? assignedSources : acceptanceReasons?.find((item) => item.type === registryType);
    const options =
      source?.values
        ?.map((v) => ({
          id: v.id,
          name: v.name ?? '',
          label: v.displayName ?? v.name ?? '',
          fullName: v.name,
          tradeName: v.tradeName,
          favourite: v.favourite
        }))
        .filter((option) => option.name)
        .sort((a, b) => {
          if (a.favourite && !b.favourite) return -1;
          if (!a.favourite && b.favourite) return 1;
          return a.name.localeCompare(b.name);
        }) ?? [];
    setTipOptions(options);
  }, [acceptanceReasons, registryType, onlyAssigned, assignedSources]);

  useEffect(() => {
    if (value?.name) {
      const match = tipOptions.find((opt) => opt.id === value.id && opt.name === value.name) ?? {
        name: value.name,
        label: value.name,
        id: value.id
      };
      setSelectedTip(match);
    } else {
      setSelectedTip(null);
    }
  }, [value?.id, value?.name, tipOptions]);

  const commitValue = (v?: ReasonType) => onChange(v);

  const handleTipChange = (_: React.SyntheticEvent<Element, Event>, v: TipOption | string | null) => {
    if (!v) {
      setSelectedTip(null);
      commitValue({ type: registryType });
      return;
    }

    if (typeof v === 'string') {
      const existingOption = tipOptions.find((opt) => opt.name === v);
      if (existingOption) {
        setSelectedTip(existingOption);
        commitValue({
          type: registryType,
          name: existingOption.name,
          id: existingOption.id
        });
      } else {
        const newOption = { name: v, label: v };
        setSelectedTip(newOption);
        commitValue({ type: registryType, name: v });
      }
      return;
    }

    if (v.label.startsWith('Přidat "') && !v.id) {
      setNewTipValue({ name: v.name });
      setDialogOpen(true);
      setSelectedTip(null);
      return;
    }

    setSelectedTip(v);
    commitValue({ type: registryType, name: v.name, id: v.id });
  };

  const handleDialogClose = () => setDialogOpen(false);

  const handleDialogConfirm = () => {
    if (!newTipValue?.name) return;

    const newOption: TipOption = {
      name: newTipValue.name,
      label: newTipValue.name
    };

    const finalCommissionValue = newTipValue.commissionValue
      ? `${newTipValue.commissionValue}${commissionType}`
      : undefined;

    commitValue({
      type: registryType,
      name: newTipValue.name,
      tradeName: newTipValue.tradeName || undefined,
      address: newTipValue.address,
      phone: newTipValue.phone,
      bankAccountNumber: newTipValue.bankAccountNumber,
      birthDate: newTipValue.birthDate,
      commissionValue: finalCommissionValue
    });

    if (!tipOptions.some((opt) => opt.name === newTipValue.name)) {
      setTipOptions([...tipOptions, newOption]);
    }

    setSelectedTip(newOption);

    setDialogOpen(false);
    setNewTipValue(undefined);
  };

  return (
    <>
      <FormControlLabel
        control={<Switch checked={onlyAssigned} onChange={(e) => setOnlyAssigned(e.target.checked)} />}
        label={isTipType ? 'Moji tipaři' : 'Moje servisy'}
      />
      <Autocomplete<TipOption, false, false, true>
        freeSolo
        options={tipOptions}
        value={selectedTip}
        onChange={handleTipChange}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        isOptionEqualToValue={(option, value) => {
          if (typeof option === 'string' || typeof value === 'string') return false;
          return option.id === value.id && option.name === value.name;
        }}
        renderInput={(params) => <TextField {...params} label={label} />}
        renderOption={(props, option) => (
          <li {...props} key={option.id ?? option.name}>
            {option.favourite && <Star sx={{ color: 'warning.main', mr: 1, fontSize: 18 }} />}
            <span>
              {option.label}
              {option.tradeName && (
                <Typography component="span" variant="caption" color="text.secondary" display="block">
                  {option.fullName}
                </Typography>
              )}
            </span>
          </li>
        )}
        filterOptions={(options, params) => {
          const filtered = options.filter((option) =>
            [option.name, option.tradeName].some((f) => f?.toLowerCase().includes(params.inputValue.toLowerCase()))
          );
          const inputValue = params.inputValue;
          const isExisting = options.some((option) => option.name === inputValue);
          if (inputValue !== '' && !isExisting) {
            return [
              ...filtered,
              {
                name: inputValue,
                label: `Přidat "${inputValue}"`
              }
            ];
          }
          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
      />

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{dialogTitle ?? (isTipType ? 'Přidat nového tipaře' : 'Přidat nový servis')}</DialogTitle>
        <DialogContent>
          <>
            <DialogContentText>
              Chcete přidat {isTipType ? 'nového tipaře' : 'nový servis'} <strong>"{newTipValue?.name}"</strong>?
            </DialogContentText>
            <Grid container spacing={2} pt={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  value={newTipValue?.name}
                  onChange={(e) =>
                    setNewTipValue((prev) => ({
                      ...prev,
                      name: e.target.value || ''
                    }))
                  }
                  label={isTipType ? 'Jméno' : 'Název'}
                  color="secondary"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  value={newTipValue?.tradeName}
                  onChange={(e) =>
                    setNewTipValue((prev) => ({
                      name: prev?.name ?? '',
                      ...prev,
                      tradeName: e.target.value
                    }))
                  }
                  label="Obchodní název"
                  color="secondary"
                  fullWidth
                  slotProps={{ htmlInput: { maxLength: 255 } }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  value={newTipValue?.phone}
                  onChange={(e) =>
                    setNewTipValue((prev) => ({
                      name: prev?.name ?? '',
                      ...prev,
                      phone: e.target.value
                    }))
                  }
                  label="Telefon"
                  color="secondary"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  value={newTipValue?.bankAccountNumber}
                  onChange={(e) =>
                    setNewTipValue((prev) => ({
                      name: prev?.name ?? '',
                      ...prev,
                      bankAccountNumber: e.target.value
                    }))
                  }
                  label="Číslo bankovního účtu"
                  color="secondary"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Datum narození"
                  value={newTipValue?.birthDate ?? null}
                  onChange={(value) =>
                    setNewTipValue((prev) => ({
                      name: prev?.name ?? '',
                      ...prev,
                      birthDate: value ?? undefined
                    }))
                  }
                  format="dd.MM.yyyy"
                  maxDate={new Date()}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={newTipValue?.address}
                  onChange={(e) =>
                    setNewTipValue((prev) => ({
                      name: prev?.name ?? '',
                      ...prev,
                      address: e.target.value
                    }))
                  }
                  label="Adresa"
                  color="secondary"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={12}>
                <Grid container spacing={2}>
                  <Grid size={9}>
                    <TextField
                      value={newTipValue?.commissionValue}
                      onChange={(e) =>
                        setNewTipValue((prev) => ({
                          name: prev?.name ?? '',
                          ...prev,
                          commissionValue: e.target.value
                        }))
                      }
                      label="Provize"
                      color="secondary"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormControl fullWidth>
                      <InputLabel
                        id="commissionType-label"
                        sx={{
                          backgroundColor: 'white',
                          px: 0.5
                        }}
                      >
                        Typ provize
                      </InputLabel>
                      <Select
                        value={commissionType}
                        onChange={(event) => setCommissionType(event.target.value)}
                        labelId="commissionType-label"
                      >
                        <MenuItem value="Kč">Kč</MenuItem>
                        <MenuItem value="%">%</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Zrušit</Button>
          <Button onClick={handleDialogConfirm} variant="contained">
            Přidat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RejectionContent: React.FC<RejectionContentProps> = ({
  reasonValue,
  onReasonChange,
  customValue,
  onCustomValueChange,
  rejectionReasons,
  accidentDate,
  onDateChange,
  acceptanceReasons,
  tipSourceValue,
  onTipSourceChange
}) => (
  <>
    <Box mb={3} mt={3}>
      <CustomDateTimePicker label="Datum" value={accidentDate} format="dd.MM.yyyy HH:mm" onChange={onDateChange} />
    </Box>

    <Typography variant="h6" fontWeight="bold" gutterBottom>
      Tipař (volitelné)
    </Typography>
    <RegistrySelector
      acceptanceReasons={acceptanceReasons}
      value={tipSourceValue}
      onChange={onTipSourceChange}
      label="Tipař"
      registryType="tip"
    />

    <Box mt={3}>
      <FormControl fullWidth>
        <Typography variant="h6" fontWeight="bold">
          Důvod odmítnutí nehody
        </Typography>
        <RadioGroup value={reasonValue?.type} onChange={(event) => onReasonChange({ type: event.target.value })}>
          {rejectionReasons?.map(({ type, label }) => (
            <FormControlLabel key={type} value={type} control={<Radio />} label={label} />
          ))}
        </RadioGroup>
        <Box mt={2}>
          <Divider />
        </Box>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Důvod"
          value={customValue}
          onChange={(e) => onCustomValueChange(e.target.value)}
        />
      </FormControl>
    </Box>
  </>
);

export const AcceptanceContent: React.FC<AcceptanceContentProps> = ({
  acceptanceReasons,
  reasonValue,
  onReasonChange,
  insuranceValue,
  onSourceDetailChange,
  onPlaceValue,
  onPlaceChange,
  onDateChange,
  accidentDate,
  isEdit = false
}) => {
  const handleInsuranceChange = (event: SelectChangeEvent<string>) => {
    const insuranceReason = acceptanceReasons?.find((item) => item.type === 'insurance');
    const insuranceValue = insuranceReason?.values.find((item) => item.id === Number.parseInt(event.target.value));
    if (insuranceValue) {
      onSourceDetailChange({
        type: 'insurance',
        name: insuranceValue.name,
        id: insuranceValue.id
      });
    }
  };

  return (
    <>
      {!isEdit && (
        <Box mb={3} mt={3}>
          <CustomDateTimePicker label="Datum" value={accidentDate} format="dd.MM.yyyy HH:mm" onChange={onDateChange} />
        </Box>
      )}
      <FormControl fullWidth>
        {!isEdit && (
          <>
            <Typography variant="h6" fontWeight="bold">
              Řešení nehody na místě?
            </Typography>
            <RadioGroup value={onPlaceValue} onChange={(event) => onPlaceChange(Number.parseInt(event.target.value))}>
              {[
                { value: 1, label: 'Ano' },
                { value: 0, label: 'Ne' }
              ].map(({ value, label }) => (
                <FormControlLabel key={value} value={value} control={<Radio />} label={label} />
              ))}
            </RadioGroup>
          </>
        )}
        <Typography variant="h6" fontWeight="bold" mt={!isEdit ? 3 : 0}>
          Zdroj ohlášení nehody
        </Typography>
        <RadioGroup value={reasonValue?.type} onChange={(event) => onReasonChange({ type: event.target.value })}>
          {acceptanceReasons?.map(({ type, label }) => (
            <FormControlLabel key={type} value={type} control={<Radio />} label={label} />
          ))}
        </RadioGroup>

        {reasonValue?.type === ACCIDENT_SOURCE_TYPE.INSURANCE && (
          <>
            <Box my={2}>
              <Divider />
            </Box>
            <Select value={insuranceValue} onChange={handleInsuranceChange}>
              {acceptanceReasons
                ?.find((item) => item.type === 'insurance')
                ?.values?.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
            </Select>
          </>
        )}

        {reasonValue?.type === ACCIDENT_SOURCE_TYPE.SERVICE && (
          <>
            <Box my={2}>
              <Divider />
            </Box>
            <RegistrySelector
              acceptanceReasons={acceptanceReasons}
              value={reasonValue?.type === 'service' ? reasonValue : undefined}
              onChange={onSourceDetailChange}
              label="Servis"
              registryType="service"
            />
          </>
        )}

        {reasonValue?.type === ACCIDENT_SOURCE_TYPE.TIP && (
          <>
            <Box my={2}>
              <Divider />
            </Box>
            <RegistrySelector
              acceptanceReasons={acceptanceReasons}
              value={reasonValue?.type === 'tip' && reasonValue?.name ? reasonValue : undefined}
              onChange={onSourceDetailChange}
              label="Tipař"
              registryType="tip"
            />
          </>
        )}
      </FormControl>
    </>
  );
};

export const AccidentCreatingStepDecision: React.FC<AccidentCreatingStepDecisionProps> = ({
  onVote,
  isLoading = false,
  rejectionReasons = [],
  acceptanceReasons = []
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState<boolean | null>(null);
  const [onPlace, setOnPlace] = useState(1);
  const [reasonValue, setReasonValue] = useState<ReasonType>();
  const [rejectionTypeValue, setRejectionTypeValue] = useState<ReasonType>();
  const [tipSource, setTipSource] = useState<ReasonType | undefined>(undefined);

  const [customValue, setCustomValue] = useState('');
  const [insuranceValue, setInsuranceValue] = useState<string>();
  const [accidentDate, setAccidentDate] = useState<Date | null>(new Date());

  const handleChoice = (vote: boolean) => {
    setSelectedChoice(vote);
    setReasonValue(undefined);
    setRejectionTypeValue(undefined);
    setTipSource(undefined);
    setCustomValue('');
    setModalOpen(true);
  };

  const handleSubmit = () => {
    if (selectedChoice === null) return;

    if (selectedChoice === true && reasonValue) {
      const choiceData: AccidentGeneral = {
        isRejected: false,
        rejectionReason: undefined,
        rejectionType: undefined,
        isSpotSolved: Boolean(onPlace),
        date: format(accidentDate ?? new Date(), 'yyyy-MM-dd HH:mm'),
        source: { ...reasonValue }
      };
      onVote(choiceData);
      setModalOpen(false);
      return;
    }

    if (selectedChoice === false && rejectionTypeValue) {
      const choiceData: AccidentGeneral = {
        isRejected: true,
        rejectionReason: customValue || undefined,
        rejectionType: rejectionTypeValue.type,
        isSpotSolved: false,
        date: format(accidentDate ?? new Date(), 'yyyy-MM-dd HH:mm'),
        source: tipSource ? { ...tipSource } : undefined
      };
      onVote(choiceData);
      setModalOpen(false);
    }
  };

  const isFormValid = () => {
    if (selectedChoice === true) {
      if (!reasonValue) return false;
      // Pokud je vybrán zdroj "tip", musí být vyplněn i konkrétní tipař
      if (reasonValue.type === ACCIDENT_SOURCE_TYPE.TIP && !reasonValue.name) {
        return false;
      }
      // Pokud je vybrán zdroj "service", musí být vyplněn i konkrétní servis
      if (reasonValue.type === ACCIDENT_SOURCE_TYPE.SERVICE && !reasonValue.name) {
        return false;
      }
      return true;
    }
    if (!rejectionTypeValue) return false;
    return !(rejectionTypeValue.type === 'different' && !customValue);
  };

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} width={1} mt={1} gap={2}>
        <DecisionCard isAccept={true} onClick={() => handleChoice(true)} label="Řeším nehodu" />
        <DecisionCard isAccept={false} onClick={() => handleChoice(false)} label="Neřeším nehodu" />
      </Stack>

      <BasicDialog open={modalOpen} onClose={() => setModalOpen(false)} title="Důvod a zdroj" maxWidth="lg">
        <DialogContent sx={{ p: 0, minWidth: { md: 600 }, maxWidth: '100%' }}>
          {selectedChoice === true ? (
            <AcceptanceContent
              reasonValue={reasonValue}
              onReasonChange={setReasonValue}
              insuranceValue={insuranceValue}
              onSourceDetailChange={setReasonValue}
              onPlaceChange={setOnPlace}
              onPlaceValue={onPlace}
              acceptanceReasons={acceptanceReasons}
              accidentDate={accidentDate}
              onDateChange={(date) => setAccidentDate(date)}
            />
          ) : selectedChoice === false ? (
            <RejectionContent
              reasonValue={rejectionTypeValue}
              onReasonChange={setRejectionTypeValue}
              customValue={customValue}
              onCustomValueChange={setCustomValue}
              rejectionReasons={rejectionReasons}
              accidentDate={accidentDate}
              onDateChange={(date) => setAccidentDate(date)}
              acceptanceReasons={acceptanceReasons}
              tipSourceValue={tipSource}
              onTipSourceChange={setTipSource}
            />
          ) : null}
          <Box mt={2}>
            <Divider />
          </Box>
          <Stack mt={3}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={isLoading || !isFormValid()}
            >
              Pokračovat
            </Button>
          </Stack>
        </DialogContent>
      </BasicDialog>
    </>
  );
};
